/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {hexToRgb} from '@ion/core/src/utils/StringUtil';
import {dlsColor} from '../../../theme/theme';

export interface InputRowProps {
  label: string;
  value: string;
  isActive: boolean;
}

interface InputRowStyleProps {
  isActive: boolean;
}

const useStyles = makeStyles(() => {
  // Grey 3 (#767676) at 10% opacity - matches the Figma "Inputs Container"
  // export's `rgba(118, 118, 118, 0.1)` exactly (118, 118, 118 is #767676
  // in decimal), so it's derived from the token rather than hardcoded ...
  const greyTint = `rgba(${hexToRgb(dlsColor('grey3')).join(', ')}, 0.1)`;
  return {
    row : {
      display    : 'flex',
      alignItems : 'stretch',
      width      : '100%',
      minHeight  : '24px'
    },
    label : {
      boxSizing      : 'border-box',
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'flex-start',
      flex           : '1 1 65%',
      padding        : '0 8px',
      borderRadius   : '4px 0 0 4px',
      borderStyle    : 'solid',
      borderColor    : greyTint,
      fontFamily     : 'Inter',
      fontWeight     : 600,
      fontSize       : '13px',
      lineHeight     : '14px',
      letterSpacing  : '0.5px',
      color          : dlsColor('grey2'),
      // Active: filled with the tint. Inactive: outlined with the same
      // tint, and no right border (it joins the value segment) ...
      background     : ({isActive}: InputRowStyleProps) => (isActive ? greyTint : 'transparent'),
      borderWidth    : ({isActive}: InputRowStyleProps) => (isActive ? '0' : '1.5px 0 1.5px 1.5px')
    },
    value : {
      boxSizing      : 'border-box',
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      flex           : '0 0 35%',
      padding        : '0 8px',
      borderRadius   : '0 4px 4px 0',
      borderStyle    : 'solid',
      borderColor    : dlsColor('grey3'),
      fontFamily     : 'Inter',
      fontWeight     : 600,
      fontSize       : '13px',
      lineHeight     : '14px',
      letterSpacing  : '0.5px',
      // Active: filled Grey 3 with white text. Inactive: outlined Grey 3
      // with Grey 2 text, no fill ...
      background     : ({isActive}: InputRowStyleProps) => (isActive ? dlsColor('grey3') : 'transparent'),
      borderWidth    : ({isActive}: InputRowStyleProps) => (isActive ? '0' : '1.5px'),
      color          : ({isActive}: InputRowStyleProps) => (isActive ? dlsColor('white') : dlsColor('grey2'))
    }
  };
});

/**
 * InputRow renders a single ION-7462 Inputs row: a label segment (left,
 * ~65% width, left-aligned text) and a value pill (right, ~35% width,
 * center-aligned text), per the Figma "Inputs Container" annotation.
 * `isActive` drives both segments together - active fills both segments
 * (Grey 3 tint on the label, solid Grey 3 with white text on the value);
 * inactive outlines both instead (same colors, as a border rather than a
 * fill).
 *
 * ```typescript
 *
 *   import {InputRow} from
 *     '@ion/dls/src/components/assetdetails/current/InputRow';
 *   ...
 *
 *   <InputRow label='Commuter Usage Button'
 *     value='Business miles'
 *     isActive />
 *
 * ```
 *
 */
export const InputRow = ({
  label,
  value,
  isActive
}: InputRowProps) => {
  const classes = useStyles({isActive});
  return (
    <div className={classes.row}>
      <span className={classes.label}>
        {label}
      </span>
      <span className={classes.value}>
        {value}
      </span>
    </div>
  );
};

export default memo(InputRow);
